import type { OnboardingCanonical, OnboardingGuidance } from "../../lib/onboarding-api";

/**
 * Macro #27 — canonical step model.
 *
 * The 6 guided steps. A step's `done` flag is derived from REAL state:
 *   1 persona   — guidance (persona chosen)
 *   2 territory — canonical (a named, owned territory is selected)
 *   3 start     — guidance (the user acknowledged how to start / has a draft)
 *   4 build     — canonical (a saved Draft exists: draft_revision > 0)
 *   5 publish   — canonical (an active publication exists)
 *   6 launch    — canonical (published = server-derived `completed`)
 *
 * `locked` reflects an unmet prerequisite; a locked step can never be "current".
 */
export type StepId = "persona" | "territory" | "start" | "build" | "publish" | "launch";

export const STEP_ORDER: StepId[] = ["persona", "territory", "start", "build", "publish", "launch"];
export const STEP_COUNT = STEP_ORDER.length;

export type StepStatus = "done" | "current" | "todo" | "locked";

export interface StepModel {
  id: StepId;
  index: number; // 1-based
  done: boolean;
  locked: boolean;
  status: StepStatus;
}

export function stepIndex(id: StepId): number {
  return STEP_ORDER.indexOf(id) + 1;
}

export function computeSteps(
  state: OnboardingGuidance | null,
  canonical: OnboardingCanonical | null,
): StepModel[] {
  const persona = Boolean(state?.persona);
  const sel = canonical?.selected_territory ?? null;
  const territoryOwned = Boolean(sel?.owned);
  const draftSaved = (sel?.draft_revision ?? 0) > 0;
  const hasPub = Boolean(sel?.has_active_publication);
  const currentStep = state?.current_step ?? 1;

  const done: Record<StepId, boolean> = {
    persona,
    territory: territoryOwned,
    // "start" is a guidance acknowledgement — done once the user moved past it
    // (advanced the guide) OR already has a saved draft.
    start: territoryOwned && (currentStep > 3 || draftSaved),
    build: draftSaved,
    publish: hasPub,
    launch: hasPub,
  };

  const locked: Record<StepId, boolean> = {
    persona: false,
    territory: !persona,
    start: !territoryOwned,
    build: !territoryOwned,
    publish: !draftSaved,
    launch: !hasPub,
  };

  const firstActionable = STEP_ORDER.find((id) => !done[id] && !locked[id]) ?? null;

  return STEP_ORDER.map((id, i) => {
    let status: StepStatus;
    if (done[id]) status = "done";
    else if (locked[id]) status = "locked";
    else if (id === firstActionable) status = "current";
    else status = "todo";
    return { id, index: i + 1, done: done[id], locked: locked[id], status };
  });
}

export function doneCount(steps: StepModel[]): number {
  return steps.filter((s) => s.done).length;
}
